import { useState, useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function IletisimPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = 'İletişim | Alfa Çilingir Beylikdüzü - 7/24 Acil Çilingir 0541 624 79 82';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Alfa Çilingir ile iletişime geçin. Beylikdüzü, Esenyurt, Büyükçekmece ve Bahçeşehir\'de 7/24 acil çilingir hizmeti için hemen arayın: 0541 624 79 82');

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/iletisim`;

    const existingScript = document.getElementById('schema-iletisim');
    if (existingScript) existingScript.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          '@id': `${siteUrl}/iletisim#webpage`,
          url: `${siteUrl}/iletisim`,
          name: 'İletişim | Alfa Çilingir',
          description: 'Alfa Çilingir iletişim sayfası. 7/24 acil çilingir hizmeti için arayın.',
          inLanguage: 'tr-TR',
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#localbusiness`,
          name: 'Alfa Çilingir',
          url: siteUrl,
          telephone: '+905416247982',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Beylikdüzü',
            addressRegion: 'İstanbul',
            addressCountry: 'TR',
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
              opens: '00:00',
              closes: '23:59',
            },
          ],
          areaServed: ['Beylikdüzü', 'Esenyurt', 'Büyükçekmece', 'Bahçeşehir'],
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'İletişim', item: `${siteUrl}/iletisim` },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.id = 'schema-iletisim';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-iletisim');
      if (s) s.remove();
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('mesaj') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d7oc5bvh9rdmu8dsspt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=locksmith%20phone%20call%20customer%20service%2C%20professional%20communication%2C%20dark%20background%2C%20warm%20lighting%2C%20Istanbul%20Turkey&width=1920&height=400&seq=cpage001&orientation=landscape"
              alt="Alfa Çilingir İletişim"
              className="w-full h-full object-cover object-top opacity-25"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
            <span className="inline-flex items-center gap-2 text-orange-400 text-xs font-bold uppercase tracking-widest mb-4">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              İletişim
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Bize Ulaşın</h1>
            <p className="text-gray-300 max-w-xl mx-auto text-base">
              Acil çilingir ihtiyacınız için hemen arayın. Mesaj göndermek için formu kullanabilirsiniz.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Info */}
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-6">İletişim Bilgileri</h2>

                <div className="space-y-5 mb-8">
                  <a href="tel:05416247982" className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-stone-100 hover:border-orange-200 transition-all cursor-pointer group">
                    <span className="w-12 h-12 flex items-center justify-center bg-orange-50 text-orange-500 rounded-xl group-hover:bg-orange-500 group-hover:text-white transition-all">
                      <i className="ri-phone-fill text-xl"></i>
                    </span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Telefon (7/24)</p>
                      <p className="font-extrabold text-gray-900 text-xl">0541 624 79 82</p>
                      <p className="text-gray-400 text-xs mt-1">Acil durumda hemen arayın</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-stone-100">
                    <span className="w-12 h-12 flex items-center justify-center bg-orange-50 text-orange-500 rounded-xl">
                      <i className="ri-map-pin-line text-xl"></i>
                    </span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Hizmet Bölgesi</p>
                      <p className="font-bold text-gray-900">Beylikdüzü, Esenyurt</p>
                      <p className="font-bold text-gray-900">Büyükçekmece, Bahçeşehir</p>
                      <p className="text-gray-400 text-xs mt-1">İstanbul Avrupa Yakası</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-stone-100">
                    <span className="w-12 h-12 flex items-center justify-center bg-orange-50 text-orange-500 rounded-xl">
                      <i className="ri-time-line text-xl"></i>
                    </span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Çalışma Saatleri</p>
                      <p className="font-bold text-gray-900">7/24 — 365 Gün</p>
                      <p className="text-gray-400 text-xs mt-1">Gece gündüz, tatil günleri dahil</p>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-2xl overflow-hidden border border-stone-100 h-56">
                  <iframe
                    title="Alfa Çilingir Konum - Beylikdüzü İstanbul"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d48084.0!2d28.64!3d41.00!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14b55f5e5e5e5e5e%3A0x0!2sBeylikd%C3%BCz%C3%BC%2C+%C4%B0stanbul!5e0!3m2!1str!2str!4v1700000000001!5m2!1str!2str"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>

              {/* Form */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Mesaj Gönderin</h2>
                <p className="text-gray-400 text-sm mb-6">Acil durumlar için lütfen telefonu arayın. Mesajlarınıza en kısa sürede dönüş yapıyoruz.</p>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <span className="w-16 h-16 flex items-center justify-center bg-green-50 text-green-500 rounded-full mb-4">
                      <i className="ri-check-double-line text-3xl"></i>
                    </span>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">Mesajınız İletildi!</h3>
                    <p className="text-gray-500 text-sm">En kısa sürede size dönüş yapacağız. Acil durumlar için <a href="tel:05416247982" className="text-orange-500 font-bold">0541 624 79 82</a>'yi arayın.</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="iletisim-formu"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ad Soyad *</label>
                        <input
                          type="text"
                          name="ad_soyad"
                          required
                          placeholder="Adınız Soyadınız"
                          className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefon *</label>
                        <input
                          type="tel"
                          name="telefon"
                          required
                          placeholder="05XX XXX XX XX"
                          className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-posta</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="ornek@email.com"
                        className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Hizmet Bölgesi</label>
                      <select
                        name="bolge"
                        className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors bg-white cursor-pointer"
                      >
                        <option value="">Bölge Seçin</option>
                        <option value="Beylikdüzü">Beylikdüzü</option>
                        <option value="Esenyurt">Esenyurt</option>
                        <option value="Büyükçekmece">Büyükçekmece</option>
                        <option value="Bahçeşehir">Bahçeşehir</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Mesajınız * <span className="text-gray-400 font-normal">({charCount}/500)</span>
                      </label>
                      <textarea
                        name="mesaj"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Çilingir ihtiyacınızı kısaca açıklayın..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || charCount > 500}
                      className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 text-sm"
                    >
                      {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
